import { useParams } from 'react-router'
import { cloneDeep } from 'lodash'
import { v4 } from 'uuid'

import { useAppSelector } from '../../../../../store/hooks'
import {
  selectPublishedEventInDb,
  selectRegistration,
  selectRegistrationByDay,
} from '../../../../../store/registration/registrationSlice'

import helpers from '../../../../../commonHelpers/helpers'

import { getConvertedData } from '../../../../../models/interface.helper'
import {
  getRegistrationsByDayUpdated,
  mergeRegistrationWithTabData,
} from '../../../../../helpers/registration-on-event'
import { getRegistrations } from '../../../../../helpers/getRegistrations'

import { RegistrationByDayModel } from '../../../../../models/registrations-by-day/registrationByDay.model'

import { IRegisterTab } from '../../../../../models/event-registered-users/event-registered-users.interface'
import { ITeamMember } from '../../../../../models/users/user.interface'
import { IManageInfo } from '../../../event-registration-tabs/EventRegistrationTabs'
import { IRegistrationByDayInterface } from '../../../../../models/registrations-by-day/registrationByDay.interface'
import ITypes, {
  ISaveRegisterTabDataFnArgs,
} from '../../../event-registration-tabs/hooks/useEventRegistrationTabs.types'

type IOnRegistrationMemberChangeFnArgs = {
  currentRow: IRegisterTab
  member: ITeamMember | null
  /** @info If remove is true then `idOfMemberToRemove` will have id in it that needs to be removed */
  isRemove: boolean
  removedUniqId?: string | null
  /** @info Id of the member this could be to delete to add as per `remove` arg */
  isManage?: boolean
  manageInfo: IManageInfo
  setManageInfo: (value: IManageInfo) => void
}

interface useRegisterTabProps {
  saveRegisterTabData: (args: ISaveRegisterTabDataFnArgs) => Promise<ISaveRegisterTabDataFnArgs>
}

const useRegisterTab = ({ saveRegisterTabData }: useRegisterTabProps) => {
  const { eventId, userId } = useParams<ITypes['IParams']>()

  const publishedEventInDb = useAppSelector(selectPublishedEventInDb)
  const registration = useAppSelector(selectRegistration)
  const registrationsByDay = useAppSelector(selectRegistrationByDay)

  const onRegistrationMemberChange = async ({
    currentRow,
    member,
    isRemove,
    removedUniqId,
    isManage,
    manageInfo,
    setManageInfo,
  }: IOnRegistrationMemberChangeFnArgs) => {
    if (!publishedEventInDb) return null

    const { EventDetails, EventFees } = publishedEventInDb
    const registrationsByDayToDelete: IRegistrationByDayInterface[] = []
    const registrationsByDayToAdd: IRegistrationByDayInterface[] = []
    const registrationsByDayManage = getRegistrations(manageInfo.register)?.allRegistrations

    const currentMember: IRegisterTab = cloneDeep(currentRow)
    const registrationsByDayConned: IRegistrationByDayInterface[] = isManage
      ? cloneDeep(registrationsByDayManage)
      : cloneDeep(registrationsByDay)

    try {
      const { emptyVarName, emptyVarValue } = helpers.findEmptyVal({
        EventDetails,
        member,
      })

      if (emptyVarName) {
        return console.error(`${emptyVarName} is ${emptyVarValue}`)
      }

      if (!isRemove) {
        currentMember.registrationsByDay.push(
          getConvertedData({
            ...new RegistrationByDayModel().toObject(),
            userId,
            eventId,
            order: currentMember.order ?? 0,
            uuid: currentMember.uuid,

            riderId: member?.memberId ?? null,
            riderDob: member?.memberDob ?? null,
            riderRole: member?.memberRole ?? null,
            riderName: member?.memberName ?? null,
            riderEmail: member?.memberEmail ?? null,
            riderProfilePicture: member?.memberProfilePicture,

            registrationDocId: registration?.id ?? null,

            registrationPrice: currentMember.price ?? 0,
            registrationByDayName: currentMember.registrationByDayName,
            qualifyFee: currentMember?.qualifyFee ?? null,
            qualifyFeeName: currentMember?.qualifyFeeName || null,
            isDivision: currentMember.children && currentMember.children.length > 0,

            eventName: EventDetails?.competitionName ?? null,

            breakTime: EventFees.rideTimeRequirement?.minutes ?? null,
            uniqId: `${v4()}-${Date.now()}`,
          })
        )
      }

      const riderToRemove = cloneDeep(
        currentMember.registrationsByDay.filter((reg) => reg.uniqId === removedUniqId)
      )
      registrationsByDayToDelete.push(...riderToRemove)

      if (riderToRemove?.[0]?.uniqId) {
        // we need to update riders which related with division
        registrationsByDayConned.forEach((registrationByDay) => {
          if (registrationByDay.divisionUniqId === riderToRemove[0].uniqId)
            registrationsByDayToDelete.push(registrationByDay)
        })
      } else {
        const newRider = cloneDeep(currentMember.registrationsByDay.slice(-1))[0]
        registrationsByDayToAdd.push(newRider)

        // we need to add riders who related with division
        if (currentMember.children && currentMember.children.length > 0) {
          currentMember.children.forEach((child) => {
            registrationsByDayToAdd.push({
              ...newRider,
              registrationPrice: child.price ?? 0,
              qualifyFee: child?.qualifyFee ?? null,
              qualifyFeeName: child?.qualifyFeeName || null,
              registrationByDayName: child.registrationByDayName,
              isDivision: false,
              divisionUniqId: newRider.uniqId,
              uuid: child.uuid,
              order: child.order ?? 0,
              uniqId: `${v4()}-${Date.now()}`,
            })
          })
        }
      }
      if (isManage) {
        const registrationsByDayUpdated = getRegistrationsByDayUpdated({
          registrationsByDayToAdd,
          registrationsByDayToDelete,
          registrationsByDay: registrationsByDayConned,
        })

        const registerTabData = mergeRegistrationWithTabData(
          manageInfo.register,
          registrationsByDayUpdated
        )

        setManageInfo({ ...manageInfo, register: registerTabData })
      } else {
        await saveRegisterTabData({
          registrationsByDayToDelete,
          registrationsByDayToAdd,
        })
      }
    } catch (error) {
      console.error(error, 'error')
    }
  }

  return { onRegistrationMemberChange }
}

export default useRegisterTab
